import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import API from '../api/api';

const font = "'Inter', sans-serif";

export default function OtpModal({ driveId, onVerified }) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef([]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const clearAndFocus = () => {
    setDigits(['', '', '', '', '', '']);
    window.setTimeout(() => inputRefs.current[0]?.focus(), 0);
  };

  const handleVerify = async () => {
    const otp = digits.join('');
    if (otp.length < 6) {
      setError('Please enter all 6 digits.');
      return;
    }

    setVerifying(true);
    setError('');
    try {
      await API.post(`/drives/${driveId}/verify-otp`, { otp });
      onVerified();
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Invalid or expired OTP. Please ask your recruiter for a new code.';
      if (String(message).toLowerCase().includes('already')) {
        onVerified();
        return;
      }
      setError(message);
      clearAndFocus();
    } finally {
      setVerifying(false);
    }
  };

  return createPortal(
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 200,
        }}
      />

      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(480px, calc(100vw - 48px))',
          background: '#ffffff',
          borderRadius: 22,
          boxShadow: '0 18px 50px rgba(0,0,0,0.18)',
          padding: '36px 40px 40px',
          zIndex: 201,
          fontFamily: font,
          boxSizing: 'border-box',
        }}
      >
        <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#55565B' }}>
          Enter OTP to Continue
        </h2>
        <p style={{ margin: '0 0 28px', fontSize: 14, color: '#85858C', lineHeight: 1.5 }}>
          Please enter the 6-digit OTP shared with you by your recruiter to begin your exam setup.
        </p>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 24 }}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(node) => { inputRefs.current[index] = node; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(event) => handleChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onPaste={index === 0 ? handlePaste : undefined}
              autoFocus={index === 0}
              style={{
                width: 48,
                height: 56,
                borderRadius: 10,
                border: `2px solid ${error ? '#C62828' : digit ? '#4D8BF1' : '#D1D5DB'}`,
                textAlign: 'center',
                fontSize: 24,
                fontWeight: 700,
                fontFamily: font,
                color: '#111827',
                outline: 'none',
                transition: 'border-color 0.15s',
                boxSizing: 'border-box',
              }}
            />
          ))}
        </div>

        {error && (
          <div style={{ fontSize: 12, color: '#C62828', marginBottom: 14, textAlign: 'center' }}>
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleVerify}
          disabled={verifying}
          style={{
            width: '100%',
            height: 46,
            background: verifying ? '#B8B8B8' : '#4D8BF1',
            color: '#ffffff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 15,
            cursor: verifying ? 'not-allowed' : 'pointer',
            fontFamily: font,
          }}
        >
          {verifying ? 'Verifying...' : 'Verify OTP'}
        </button>
      </div>
    </>,
    document.body
  );
}
